import { I18n } from '@affine/i18n';
import { resolveColor } from '@blocksuite/affine-model';
import {
  FontLoaderService,
  isCanvasHandwritingFontReady,
  UserAvatarCacheProvider,
  UserListProvider,
  UserProvider,
  waitForCanvasHandwritingFont,
} from '@blocksuite/affine-shared/services';
import { ThemeProvider } from '@blocksuite/affine-shared/services';
import { BlockComponent, toGfxBlockComponent } from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { css, html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import type {
  StickyNoteBlockModel,
  StickyNoteColor,
} from '../schemas/sticky-note-model.js';
import { StickyNoteBlockSchema } from '../schemas/sticky-note-model.js';
import {
  renderStickyNotePaper,
  STICKY_NOTE_PAPER_PATHS,
} from './sticky-note-visual.js';

export const AFFINE_STICKY_NOTE_CANVAS = 'affine-sticky-note-canvas';

export function shouldDeferStickyNoteText(
  fontReady: boolean,
  isPeekPreview: boolean
) {
  return !fontReady && !isPeekPreview;
}

export function stickyNoteTextClipPath(clipId: string, isPeekPreview: boolean) {
  // Chromium can drop native textarea glyphs when an objectBoundingBox SVG
  // clip is composed inside the transformed, read-only edgeless peek view.
  // The paper SVG already provides the visible silhouette, and the padded
  // textarea does not reach its warped edges, so the preview does not need
  // the redundant compositor clip.
  return isPeekPreview ? 'none' : `url(#${clipId})`;
}

export class StickyNoteBlockComponent extends BlockComponent<StickyNoteBlockModel> {
  override renderBlock() {
    return nothing;
  }
}

export class StickyNoteCanvasBlockComponent extends toGfxBlockComponent(
  StickyNoteBlockComponent
) {
  static override styles = css`
    affine-sticky-note-canvas {
      pointer-events: all;
    }
    affine-sticky-note-canvas .paper-shell {
      position: relative;
      width: 100%;
      height: 100%;
    }
    affine-sticky-note-canvas .paper {
      position: absolute;
      inset: 0;
      box-sizing: border-box;
      padding: 30px 24px 46px;
      color: var(--sticky-text-color);
      background: transparent;
      overflow: hidden;
    }
    affine-sticky-note-canvas textarea {
      position: relative;
      z-index: 1;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      resize: none;
      border: 0;
      outline: 0;
      background: transparent;
      color: inherit;
      font:
        600 21px/1.32 Kalam,
        'Comic Sans MS',
        cursive;
      overflow: auto;
      align-content: center;
      text-align: center;
      text-wrap: balance;
    }
    affine-sticky-note-canvas .paper-shell[data-editing='false'] textarea {
      pointer-events: none;
      user-select: none;
    }
    affine-sticky-note-canvas .paper-shell[data-text-deferred='true'] textarea {
      visibility: hidden;
    }
    affine-sticky-note-canvas
      .paper-shell[data-peek-preview='true'][data-font-ready='false']
      textarea {
      font-family: 'Comic Sans MS', cursive;
    }
    affine-sticky-note-canvas .assignee {
      position: absolute;
      z-index: 2;
      right: 12px;
      bottom: 22px;
    }
    affine-sticky-note-canvas
      .paper-shell[data-unassigned='true']
      affine-member-picker::part(trigger) {
      opacity: 0;
      pointer-events: none;
    }
    affine-sticky-note-canvas
      .paper-shell:hover
      affine-member-picker::part(trigger),
    affine-sticky-note-canvas affine-member-picker:focus-within::part(trigger) {
      opacity: 1 !important;
      pointer-events: auto !important;
    }
  `;

  @state() private accessor _editing = false;
  @state() private accessor _fontReady = isCanvasHandwritingFontReady();

  override connectedCallback() {
    super.connectedCallback();
    const selection = this.gfx.selection;
    this._editing = selection.has(this.model.id) && selection.editing;
    this.disposables.add(
      selection.slots.updated.subscribe(() => {
        this._editing = selection.has(this.model.id) && selection.editing;
      })
    );
    void waitForCanvasHandwritingFont(
      this.std.getOptional(FontLoaderService)
    ).then(
      () => {
        if (this.isConnected) this._fontReady = true;
      },
      () => {
        if (this.isConnected) this._fontReady = true;
      }
    );
  }

  private _setAssignee(assigneeId: string) {
    if (assigneeId === this.model.props.assigneeId) return;
    this.store.updateBlock(this.model, { assigneeId });
    if (!assigneeId) return;
    this.dispatchEvent(
      new CustomEvent('streamlain:sticky-note-assigned', {
        bubbles: true,
        composed: true,
        detail: {
          assigneeId,
          blockId: this.model.id,
          blockPreview: this.model.props.text.trim() || 'Sticky note',
        },
      })
    );
  }

  private _enterEditing(event: Event) {
    event.stopPropagation();
    this.gfx.selection.set({ elements: [this.model.id], editing: true });
  }

  private _stopTextInputHotkeys(event: KeyboardEvent) {
    event.stopImmediatePropagation();
  }

  override renderGfxBlock() {
    const theme = this.std.get(ThemeProvider).edgeless$.value;
    const isPeekPreview =
      this.closest('[data-streamlain-peek-preview="true"]') !== null;
    const textColor = resolveColor(
      this.model.props.textColor,
      theme,
      '#312d27'
    );
    const clipId = `sticky-paper-${this.model.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    return html`<div
      class="paper-shell"
      style=${styleMap({
        '--sticky-text-color': textColor,
        transform: `rotate(${this.model.rotate}deg)`,
        transformOrigin: 'center',
      })}
      data-color=${this.model.props.color as StickyNoteColor}
      data-paper-variant=${this.model.props.paperVariant}
      data-unassigned=${!this.model.props.assigneeId}
      data-editing=${this._editing}
      data-font-ready=${this._fontReady}
      data-peek-preview=${isPeekPreview}
      data-text-deferred=${shouldDeferStickyNoteText(
        this._fontReady,
        isPeekPreview
      )}
    >
      ${renderStickyNotePaper({
        color: this.model.props.color,
        height: '100%',
        paperVariant: this.model.props.paperVariant,
        pinOffset: this.model.props.pinOffset ?? 0,
        pinOffsetY: this.model.props.pinOffsetY ?? 0,
        pinRotation: this.model.props.pinRotation ?? 0,
        position: 'absolute',
        rotation: 0,
        visualId: `sticky-paper-visual-${this.model.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`,
        width: '100%',
      })}
      <svg width="0" height="0" aria-hidden="true">
        <defs>
          <clipPath id=${clipId} clipPathUnits="objectBoundingBox">
            <path d=${this._paperPath}></path>
          </clipPath>
        </defs>
      </svg>
      <div
        class="paper"
        style=${styleMap({
          clipPath: stickyNoteTextClipPath(clipId, isPeekPreview),
        })}
        data-color=${this.model.props.color}
      >
        <textarea
          aria-label=${I18n.t('com.streamlain.canvas.sticky-note.text-label')}
          placeholder=${I18n.t('com.streamlain.canvas.sticky-note.placeholder')}
          .readOnly=${!this._editing}
          .value=${this.model.props.text}
          style=${styleMap({
            fontSize: `${this.model.props.fontSize ?? 21}px`,
          })}
          @input=${(event: InputEvent) =>
            this.store.updateBlock(this.model, {
              text: (event.target as HTMLTextAreaElement).value,
            })}
          @pointerdown=${(event: Event) => event.stopPropagation()}
          @focus=${this._enterEditing}
          @keydown=${this._stopTextInputHotkeys}
          @keyup=${this._stopTextInputHotkeys}
        ></textarea>
      </div>
      <div class="assignee">
        <affine-member-picker
          variant="sticky"
          .avatarCache=${this.std.getOptional(UserAvatarCacheProvider)}
          .selectedId=${this.model.props.assigneeId}
          .userListService=${this.std.getOptional(UserListProvider)}
          .userService=${this.std.getOptional(UserProvider)}
          @member-change=${(event: CustomEvent<{ memberId: string }>) =>
            this._setAssignee(event.detail.memberId)}
        ></affine-member-picker>
      </div>
    </div>`;
  }

  private get _paperPath() {
    return STICKY_NOTE_PAPER_PATHS[this.model.props.paperVariant % 4];
  }
}

export const StickyNoteBlockInteraction =
  GfxViewInteractionExtension<StickyNoteCanvasBlockComponent>(
    StickyNoteBlockSchema.model.flavour,
    {
      resizeConstraint: { minWidth: 160, minHeight: 130 },
      handleResize: () => ({
        onResizeStart(context) {
          context.default(context);
        },
        onResizeMove(context) {
          context.default(context);
        },
        onResizeEnd(context) {
          context.default(context);
        },
      }),
      handleSelection: ({ gfx, view, model }) => ({
        onSelect(context) {
          const { selected, multiSelect } = context;
          const alreadySelected = gfx.selection.has(model.id);
          if (!multiSelect && selected && alreadySelected) {
            if (model.isLocked() || gfx.selection.editing) return;
            gfx.selection.set({ elements: [model.id], editing: true });
            void view.updateComplete.then(() => {
              if (!view.isConnected) return;
              view.shadowRoot?.querySelector('textarea')?.focus();
            });
            return;
          }
          context.default(context);
        },
      }),
    }
  );

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_STICKY_NOTE_CANVAS]: StickyNoteCanvasBlockComponent;
  }
}
